import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Clock, ArrowRight, Lock, UserCheck } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ADMIN_PASSWORD = "Schnucki/88";

export const EmployeeLogin = ({ onLogin }) => {
  const [employeeCode, setEmployeeCode] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // Check if employee is already logged in
    const savedCode = localStorage.getItem("zeitflow_employee_code");
    const savedName = localStorage.getItem("zeitflow_employee_name");
    const savedCompany = localStorage.getItem("zeitflow_company_name");
    const savedCompanyCode = localStorage.getItem("zeitflow_company_code");
    const savedIsAdmin = localStorage.getItem("zeitflow_is_admin") === "true";
    if (savedCode && savedName) {
      onLogin(savedCode, savedName, savedCompany, savedIsAdmin, savedCompanyCode);
    }
  }, [onLogin]);

  const isAdminCode = employeeCode.toUpperCase() === "ADMIN";

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!employeeCode.trim()) return;
    
    const code = employeeCode.trim().toUpperCase();
    
    // Admin login
    if (code === "ADMIN") {
      if (password !== ADMIN_PASSWORD) {
        toast.error("Falsches Passwort");
        return;
      }
      localStorage.setItem("zeitflow_employee_code", "ADMIN");
      localStorage.setItem("zeitflow_employee_name", "Administrator");
      localStorage.setItem("zeitflow_company_name", "Administrator");
      localStorage.setItem("zeitflow_is_admin", "true");
      onLogin("ADMIN", "Administrator", "Administrator", true);
      return;
    }
    
    // Employee login with code + password
    if (!password.trim()) {
      toast.error("Bitte Passwort eingeben");
      return;
    }
    
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/employees/login`, {
        code: code,
        password: password.trim()
      });
      const employee = response.data;
      
      localStorage.setItem("zeitflow_employee_code", employee.code);
      localStorage.setItem("zeitflow_employee_name", employee.name);
      localStorage.setItem("zeitflow_company_name", employee.company_name);
      localStorage.setItem("zeitflow_company_code", employee.company);
      localStorage.setItem("zeitflow_is_admin", "false");
      onLogin(employee.code, employee.name, employee.company_name, false, employee.company);
      toast.success(`Willkommen, ${employee.name}!`);
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error("Falsches Passwort");
      } else if (error.response?.status === 404) {
        toast.error("Mitarbeiter-Code nicht gefunden");
      } else {
        toast.error("Anmeldung fehlgeschlagen");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-6">
      <Card className="w-full max-w-md zeitflow-card animate-fadeIn" data-testid="employee-login-card">
        <CardHeader className="text-center pb-2">
          <div className="w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Clock className="w-8 h-8 text-white" />
          </div>
          <CardTitle className="text-2xl font-bold text-slate-900 font-['Manrope']">
            ZeitFlow
          </CardTitle>
          <p className="text-slate-500 mt-2">
            Arbeitszeiterfassung
          </p>
        </CardHeader>
        <CardContent className="pt-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="employee_code" className="text-sm font-medium text-slate-700">
                <UserCheck className="w-4 h-4 inline mr-1 text-blue-600" />
                Mitarbeiter-Code
              </Label>
              <Input
                id="employee_code"
                type="text"
                value={employeeCode}
                onChange={(e) => setEmployeeCode(e.target.value.toUpperCase())}
                placeholder="z.B. MA001 oder ADMIN"
                className="h-12 font-mono tracking-wider text-center text-lg"
                data-testid="employee-code-input"
                autoFocus
              />
            </div>
            
            {/* Password field - always shown */}
            <div className="space-y-2 animate-fadeIn">
              <Label htmlFor="password" className="text-sm font-medium text-slate-700">
                <Lock className="w-4 h-4 inline mr-1 text-blue-600" />
                Passwort {!isAdminCode && <span className="text-slate-400 font-normal">(6-stellig)</span>}
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={isAdminCode ? "Admin-Passwort" : "6-stelliges Passwort"}
                className="h-12 text-center tracking-widest"
                data-testid="password-input"
                maxLength={isAdminCode ? undefined : 6}
              />
              {!isAdminCode && (
                <p className="text-xs text-slate-500">
                  Ihr Passwort wurde vom Administrator vergeben
                </p>
              )}
            </div>
            
            <Button
              type="submit"
              className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-lg"
              disabled={!employeeCode.trim() || !password.trim() || isLoading}
              data-testid="employee-login-button"
            >
              {isLoading ? "Wird geprüft..." : "Anmelden"}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default EmployeeLogin;
